import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { MedConteggio, MedDispositivo, MedModello } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { livelloModulo } from '../auth/permessi'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from '../components/common'
import { DataTable } from '../components/DataTable'
import { BadgeStatoMed, etichettaStatoMed } from '../components/med/comuni'
import { FinestraBlocco, FinestraModello } from '../components/med/Finestre'
import { ImportCmms } from '../components/med/ImportCmms'
import { VistaCollegamenti, VistaDove, VistaUso } from '../components/med/Contesto'
import { VistaFonti, VistaRichiami } from '../components/med/Richiami'
import { VistaDisponibilita } from '../components/med/Disponibilita'
import { MappaClinica } from '../components/med/MappaClinica'
import { VistaIdentitaFilo } from '../components/med/IdentitaFilo'
import { VistaSistemi } from '../components/med/Sistemi'
import { VistaVerifiche } from '../components/med/Verifiche'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * CENSO MED (ADR-0027): the medical devices area, for clinical engineering.
 *
 * Only the CIs CENSO recognised as medical devices, each with the record that
 * clinical engineering fills in. The views follow the menu: Panoramica (the
 * numbers, each one opens the filtered list) and Dispositivi (the list). The
 * device page is `MedDevice`. Everything is read from `/medical`: the role of
 * clinical engineering does not see the rest of the CMDB.
 */

/** The filters a number of the overview opens the list with (`?stato=…`). */
const FILTRI = ['stato', 'marca', 'categoria', 'reparto', 'sede', 'solo'] as const

function filtra(righe: MedDispositivo[], parametri: URLSearchParams): MedDispositivo[] {
  const stato = parametri.get('stato')
  const marca = parametri.get('marca')
  const categoria = parametri.get('categoria')
  const reparto = parametri.get('reparto')
  const sede = parametri.get('sede')
  const solo = parametri.get('solo')
  return righe.filter((r) =>
    (stato === null || r.status === stato)
    && (marca === null || r.vendor === marca)
    && (categoria === null || r.category === categoria)
    && (reparto === null || (r.org_unit?.name ?? '') === reparto)
    && (sede === null || (r.location?.name ?? '') === sede)
    && (solo !== 'senza-numero' || !r.inventory_number)
    && (solo !== 'senza-sede' || (!r.location && !r.org_unit))
    && (solo !== 'avvisi' || r.advisories > 0)
    && (solo !== 'senza-modello' || !r.model))
}

function Panoramica() {
  const dati = useAsync(() => api.medOverview(), [])
  const o = dati.data
  if (dati.loading && !o) return <Spinner />
  if (!o) return <ErrorBanner error={dati.error} />
  if (o.devices === 0) {
    return (
      <EmptyState
        title={t('Nessun elettromedicale riconosciuto finora.')}
        hint={t('I dispositivi compaiono qui quando CENSO li riconosce come elettromedicali (inventario passivo, riconoscimento, inventari di riferimento).')}
      />
    )
  }
  const elenco = (chiave: string, valore: string) => `/medical/devices?${chiave}=${encodeURIComponent(valore)}`
  const ripartizione = (titolo: string, righe: MedConteggio[], chiave: string, etichetta?: (v: string) => string) => (
    <Card title={titolo}>
      <DataTable
        rows={righe}
        rowKey={(r) => r.value}
        initialSort={{ key: 'count', dir: 'desc' }}
        paginazione={false}
        strumenti={false}
        columns={[
          {
            key: 'value', header: t('Voce'), value: (r) => (etichetta ? etichetta(r.value) : r.value) || t('(non indicato)'),
            render: (r) => <Link to={elenco(chiave, r.value)}>{(etichetta ? etichetta(r.value) : r.value) || t('(non indicato)')}</Link>,
          },
          { key: 'count', header: t('Dispositivi'), type: 'number', align: 'right', value: (r) => r.count },
        ]}
      />
    </Card>
  )
  return (
    <>
      <div className="wazuh-kpi">
        <StatCard label={t('Elettromedicali')} value={o.devices} to="/medical/devices" />
        <StatCard label={t('Senza numero d’inventario')} value={o.unregistered} tone={o.unregistered ? 'warn' : 'ok'}
          to="/medical/devices?solo=senza-numero" />
        <StatCard label={t('Senza reparto né sede')} value={o.without_location} tone={o.without_location ? 'warn' : 'ok'}
          to="/medical/devices?solo=senza-sede" />
        <StatCard label={t('Con avvisi di sicurezza')} value={o.with_advisories} tone={o.with_advisories ? 'error' : 'ok'}
          to="/medical/devices?solo=avvisi" />
        <StatCard label={t('Richiami da gestire')} value={o.recalls_to_handle} tone={o.recalls_to_handle ? 'error' : 'ok'}
          to="/medical/recalls" />
        <StatCard label={t('Modelli')} value={o.models} to="/medical/models" />
        <StatCard label={t('Modello da determinare')} value={o.without_model} tone={o.without_model ? 'warn' : 'ok'}
          hint={t('si conosce solo la serie')} to="/medical/devices?solo=senza-modello" />
        <StatCard label={t('Sistemi clinici')} value={o.clinical_systems ?? 0} hint={t('PACS, RIS, HL7: computer, non elettromedicali')}
          to="/medical/systems" />
        {(o.external ?? 0) > 0 && (
          <StatCard label={t('Dispositivi esterni')} value={o.external ?? 0}
            hint={t('visti solo via PACS/archivio, spesso di altre strutture')} to="/medical/devices?solo=esterni" />
        )}
        {o.excluded > 0 && (
          <StatCard label={t('Esclusi a mano')} value={o.excluded} hint={t('segnati «non è un elettromedicale»')} />
        )}
      </div>
      <div className="med-ripartizioni">
        {ripartizione(t('Per stato'), o.by_status, 'stato', etichettaStatoMed)}
        {ripartizione(t('Per reparto'), o.by_org_unit, 'reparto')}
        {ripartizione(t('Per sede'), o.by_location, 'sede')}
        {ripartizione(t('Per produttore'), o.by_vendor, 'marca')}
        {ripartizione(t('Per categoria'), o.by_category, 'categoria')}
      </div>
    </>
  )
}

function Dispositivi() {
  const { principal } = useAuth()
  const scrive = livelloModulo(principal, 'medical') === 'write'
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [blocco, setBlocco] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [parametri, setParametri] = useSearchParams()
  const [fuori, setFuori] = useState(false)
  const [esclusi, setEsclusi] = useState(false)
  const [dismessi, setDismessi] = useState(false)
  // «?solo=esterni»: the devices identified only through a PACS/archive (other
  // institutions', stored), catalogued apart from the hospital's own.
  const esterni = parametri.get('solo') === 'esterni'
  const dati = useAsync(
    () => api.medDevices({ includeOut: fuori, includeExcluded: esclusi, includeRetired: dismessi, external: esterni }),
    [fuori, esclusi, dismessi, esterni],
  )
  const righe = useMemo(() => filtra(dati.data?.devices ?? [], parametri), [dati.data, parametri])
  const filtriAttivi = FILTRI.filter((f) => parametri.has(f))
  const togliFiltri = () => {
    const resto = new URLSearchParams(parametri)
    FILTRI.forEach((f) => resto.delete(f))
    setParametri(resto, { replace: true })
  }
  return (
    <>
      <div className="med-opzioni">
        <label className="checkbox">
          <input type="checkbox" checked={fuori} onChange={(e) => setFuori(e.target.checked)} />
          {t('Anche quelli non più elettromedicali')}
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={esclusi} onChange={(e) => setEsclusi(e.target.checked)} />
          {t('Anche gli esclusi a mano')}
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={dismessi} onChange={(e) => setDismessi(e.target.checked)} />
          {t('Anche i dismessi')}
        </label>
        {filtriAttivi.length > 0 && (
          <button type="button" className="secondary" onClick={togliFiltri}>
            {t('Togli il filtro della panoramica')}
          </button>
        )}
        <BottoniEsporta nome="elettromedicali"
          path={(fmt) => api.medDevicesExportPath(fmt, { includeOut: fuori, includeExcluded: esclusi, includeRetired: dismessi })} />
      </div>
      {scrive && scelti.size > 0 && (
        <div className="med-barra-selezione" role="region" aria-label={t('Azioni sui dispositivi scelti')}>
          <strong>{t('{n} dispositivi scelti', { n: scelti.size })}</strong>
          <button type="button" onClick={() => setBlocco(true)}>{t('Modifica in blocco…')}</button>
          <button type="button" className="secondary" onClick={() => setScelti(new Set())}>{t('Deseleziona')}</button>
        </div>
      )}
      {esito && <p className="field__hint" style={{ color: 'var(--ok)' }}>{esito}</p>}
      {blocco && (
        <FinestraBlocco ids={[...scelti]} onChiudi={() => setBlocco(false)}
          onFatto={(n) => {
            setBlocco(false)
            setScelti(new Set())
            setEsito(t('Modificati {n} dispositivi.', { n }))
            void dati.reload()
          }} />
      )}
      <ErrorBanner error={dati.error} />
      {dati.data?.truncated && (
        <p className="field__hint">{t('Elenco troncato: sono mostrati i primi {n} dispositivi.', { n: dati.data.devices.length })}</p>
      )}
      {esterni && (
        <p className="field__hint">
          {t('Dispositivi identificati solo attraverso un PACS/archivio (un indirizzo che riceve immagini da molte modalità, spesso di altre strutture): sono studi conservati o referti arrivati da fuori, catalogati a parte e non contati fra gli elettromedicali dell’ospedale.')}
        </p>
      )}
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable
          id="medical-devices"
          rows={righe}
          rowKey={(r) => r.id}
          initialSort={{ key: 'name' }}
          conteggio={{ nome: t('dispositivi') }}
          nomeEsportazione="elettromedicali"
          emptyText={t('Nessun elettromedicale in questo elenco.')}
          selezione={scrive ? { chiavi: scelti, onCambia: setScelti, id: (r) => r.id } : undefined}
          columns={[
            { key: 'name', header: t('Nome'), value: (r) => r.name,
              render: (r) => <><Link to={`/medical/devices/${r.id}`}>{r.name || '—'}</Link>{r.external && <span className="badge badge--muted" style={{ marginLeft: 6 }} title={t('Identificato solo attraverso un PACS/archivio, spesso di un’altra struttura: non è un apparecchio dell’ospedale.')}>{t('esterno')}</span>}</> },
            { key: 'inventory_number', header: t('N. inventario'), mono: true, value: (r) => r.inventory_number ?? '' },
            { key: 'vendor', header: t('Produttore'), filter: 'select', value: (r) => r.vendor },
            { key: 'series', header: t('Serie'), filter: 'select', value: (r) => r.series },
            { key: 'model', header: t('Modello'), value: (r) => r.model || (r.series ? t('da determinare') : ''),
              render: (r) => r.model || <span className="text-muted">{r.series ? t('da determinare') : '—'}</span> },
            { key: 'category', header: t('Categoria'), filter: 'select', value: (r) => r.category },
            { key: 'status', header: t('Stato'), filter: 'select', value: (r) => etichettaStatoMed(r.status), render: (r) => <BadgeStatoMed stato={r.status} /> },
            { key: 'org_unit', header: t('Reparto'), filter: 'select', value: (r) => r.org_unit?.name ?? '' },
            { key: 'location', header: t('Sede'), value: (r) => r.location?.name ?? '', hidden: true },
            { key: 'room', header: t('Stanza'), value: (r) => [r.room, r.bed].filter(Boolean).join(' · '), hidden: true },
            { key: 'ip', header: t('Indirizzo IP'), mono: true, value: (r) => r.ip ?? '' },
            { key: 'mac', header: t('MAC'), mono: true, value: (r) => r.mac ?? '', hidden: true },
            { key: 'serial_number', header: t('Numero di serie'), mono: true, value: (r) => r.serial_number ?? '', hidden: true },
            { key: 'advisories', header: t('Avvisi'), type: 'number', align: 'right', value: (r) => r.advisories,
              render: (r) => (r.advisories ? <span className="badge badge--error">{r.advisories}</span> : '—') },
            { key: 'last_seen', header: t('Ultimo contatto'), type: 'date', value: (r) => r.last_seen ?? '', render: (r) => formatDateTime(r.last_seen) },
          ]}
        />
      )}
    </>
  )
}

function Modelli() {
  const dati = useAsync(() => api.medModels(), [])
  const [aperto, setAperto] = useState<string | null>(null)
  const nome = (m: MedModello) => m.model || (m.series ? t('{s} (modello da determinare)', { s: m.series }) : t('Modello non indicato'))
  return (
    <>
      <div className="med-opzioni">
        <BottoniEsporta nome="modelli-elettromedicali" path={(fmt) => api.medModelsExportPath(fmt)} />
      </div>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable
          id="medical-models"
          rows={dati.data?.models ?? []}
          rowKey={(m) => m.key}
          initialSort={{ key: 'devices', dir: 'desc' }}
          conteggio={{ nome: t('modelli') }}
          nomeEsportazione="modelli-elettromedicali"
          emptyText={t('Nessun modello: i dispositivi non sono ancora stati riconosciuti.')}
          columns={[
            { key: 'vendor', header: t('Produttore'), filter: 'select', value: (m) => m.vendor },
            { key: 'series', header: t('Serie'), filter: 'select', value: (m) => m.series },
            { key: 'model', header: t('Modello'), value: (m) => nome(m),
              render: (m) => <button type="button" className="linklike" onClick={() => setAperto(m.key)}>{nome(m)}</button> },
            { key: 'category', header: t('Categoria'), filter: 'select', value: (m) => m.category },
            { key: 'devices', header: t('Dispositivi'), type: 'number', align: 'right', value: (m) => m.devices },
            { key: 'in_use', header: t('In uso'), type: 'number', align: 'right', value: (m) => m.in_use },
            { key: 'out_of_service', header: t('Fermi o in riparazione'), type: 'number', align: 'right', value: (m) => m.out_of_service },
            { key: 'unregistered', header: t('Senza n. inventario'), type: 'number', align: 'right', value: (m) => m.unregistered, hidden: true },
            { key: 'wards', header: t('Reparti'), value: (m) => m.wards.join(', ') },
            { key: 'with_advisories', header: t('Con avvisi'), type: 'number', align: 'right', value: (m) => m.with_advisories,
              render: (m) => (m.with_advisories ? <span className="badge badge--error">{m.with_advisories}</span> : '—') },
            { key: 'cnd', header: t('CND'), mono: true, value: (m) => m.registry?.cnd_code ?? '', hidden: true },
            { key: 'end_of_sale', header: t('Fuori commercio dal'), type: 'date', value: (m) => m.registry?.end_of_sale ?? '',
              render: (m) => (m.registry?.end_of_sale ? formatDateTime(m.registry.end_of_sale).slice(0, 10) : '—') },
            { key: 'risk_class', header: t('Classe di rischio'), value: (m) => m.catalogue?.risk_class ?? '', hidden: true },
            { key: 'mdr_class', header: t('Classe di rischio (MDR)'), filter: 'select', value: (m) => m.eudamed?.risk_class ?? '', hidden: true },
            { key: 'emdn', header: t('Nomenclatura EMDN'), mono: true, value: (m) => m.eudamed?.emdn_code ?? '', hidden: true },
            { key: 'fda_class', header: t('Classe FDA'), value: (m) => m.fda?.device_class ?? '', hidden: true },
            { key: 'fda_product_code', header: t('Codice prodotto FDA'), mono: true, value: (m) => m.fda?.product_code ?? '', hidden: true },
            { key: 'eol_date', header: t('Fine vita'), type: 'date', value: (m) => m.catalogue?.eol_date ?? '', hidden: true },
          ]}
        />
      )}
      {aperto && <FinestraModello chiave={aperto} onChiudi={() => setAperto(null)} />}
    </>
  )
}

export default function MedArea() {
  const { vista } = useParams()
  const devices = vista === 'devices'
  const models = vista === 'models'
  const importa = vista === 'import'
  const altre: Record<string, { titolo: string; sotto: string; vista: JSX.Element }> = {
    where: {
      titolo: t('Dove sono'),
      sotto: t('Dove la rete vede ogni elettromedicale (switch, porta, VLAN, cosa dice di sé) e i suggerimenti per reparto, sede, stanza e letto.'),
      vista: <VistaDove />,
    },
    connections: {
      titolo: t('Collegamenti'),
      sotto: t('Con chi parlano gli elettromedicali: PACS, integrazioni HL7, centrali, server, indirizzi esterni.'),
      vista: <VistaCollegamenti />,
    },
    map: {
      titolo: t('Mappa clinica'),
      sotto: t('Elettromedicali e sistemi clinici, e chi parla con chi: dai flussi e dagli agenti CENSO sui sistemi clinici. Indirizzi, porte e programmi, mai il contenuto. Doppio clic su un nodo per aprirlo.'),
      vista: <MappaClinica altezza={680} />,
    },
    'wire-identity': {
      titolo: t('Identità dal filo'),
      sotto: t('Gli apparecchi riconosciuti dai messaggi DICOM e HL7 che la sonda vede sul filo: solo l’identità dell’apparato (costruttore, modello, numero di serie, UDI, software), mai dati del paziente. Da attivare per ospedale.'),
      vista: <VistaIdentitaFilo />,
    },
    systems: {
      titolo: t('Sistemi clinici'),
      sotto: t('I sistemi con cui lavorano gli elettromedicali: PACS, RIS, motori HL7, middleware di laboratorio, postazioni di refertazione. Sono computer, non elettromedicali, e non entrano nei conteggi dei dispositivi.'),
      vista: <VistaSistemi />,
    },
    checks: {
      titolo: t('Manutenzioni e verifiche'),
      sotto: t('Le scadenze delle manutenzioni e delle verifiche di sicurezza elettrica (CEI EN 62353) di tutti gli elettromedicali, e chi riceve i promemoria.'),
      vista: <VistaVerifiche />,
    },
    recalls: {
      titolo: t('Richiami e avvisi'),
      sotto: t('I richiami e gli avvisi di sicurezza delle autorità sui modelli dei vostri elettromedicali, e gli avvisi di sicurezza informatica sui vostri dispositivi: come li gestite.'),
      vista: <VistaRichiami />,
    },
    sources: {
      titolo: t('Paese e basi dati'),
      sotto: t('Il Paese dell’ospedale accende le basi dati pubbliche che gli servono (registri dei dispositivi, richiami); ognuna si può accendere o spegnere a mano.'),
      vista: <VistaFonti />,
    },
    availability: {
      titolo: t('Disponibilità'),
      sotto: t('Quali elettromedicali sono in uso o liberi adesso, e dove si trovano: per trovare un monitor o un ecografo libero, o raggiungere un dispositivo richiamato.'),
      vista: <VistaDisponibilita />,
    },
    usage: {
      titolo: t('Utilizzo'),
      sotto: t('Quanto si usa ogni elettromedicale, dall’attività di rete degli ultimi 30 giorni.'),
      vista: <VistaUso />,
    },
  }
  const altra = vista ? altre[vista] : undefined
  if (altra) {
    return (
      <>
        <header className="page__header">
          <div>
            <h1 className="page__title">{altra.titolo}</h1>
            <p className="page__subtitle">{altra.sotto}</p>
          </div>
        </header>
        {altra.vista}
      </>
    )
  }
  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">
            {devices ? t('Dispositivi') : models ? t('Catalogo modelli') : importa ? t('Importa dal CMMS') : t('CENSO MED')}
          </h1>
          <p className="page__subtitle">
            {devices
              ? t('Gli elettromedicali riconosciuti da CENSO, con la scheda dell’Ingegneria clinica.')
              : models
                ? t('I modelli degli elettromedicali dell’ospedale: quanti sono, dove, in che stato, con i dati del catalogo.')
                : importa
                  ? t('L’elenco dell’Ingegneria clinica diventa una serie di proposte per le schede: si sceglie cosa applicare.')
                  : t('Gli elettromedicali dell’ospedale: quanti sono, cosa resta da registrare, dove sono, cosa va controllato.')}
          </p>
        </div>
      </header>
      {devices ? <Dispositivi /> : models ? <Modelli /> : importa ? <ImportCmms /> : <Panoramica />}
    </>
  )
}
